import { authService } from './auth.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface Deposit {
  id: string;
  from_wallet_id: string;
  to_wallet_id: string;
  amount: number;
  currency_type_id: string;
  currency_name?: string;
  status: string;
  notes?: string;
  deposit_date: number;
  deposited_by: string;
  deposited_by_name?: string;
  location_id?: string;
  location_name?: string;
  created_at: number;
}

export interface LocationBalance {
  location_id: string;
  location_name: string;
  location_type: string;
  department_name?: string;
  currency_type_id?: string;
  currency_name?: string;
  wallet_id?: string;
  balance?: number;
}

export interface CashClosing {
  id: string;
  location_id: string;
  location_name?: string;
  currency_type_id?: string;
  currency_name?: string;
  closing_date: number;
  closing_number: string;
  system_balance: number;
  physical_count: number;
  difference: number;
  justification?: string;
  status: 'open' | 'closed' | 'approved';
  closed_by: string;
  closed_by_name?: string;
  approved_by?: string;
  approved_by_name?: string;
  approved_at?: number;
  created_at: number;
}

export const internalDepositService = {
  // Depósitos
  async createDeposit(data: {
    from_wallet_id: string;
    to_location_id: string;
    currency_type_id: string;
    amount: number;
    notes?: string;
  }): Promise<any> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/internal-deposits`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  async listMyDeposits(limit = 50): Promise<Deposit[]> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/internal-deposits/my-deposits?limit=${limit}`
    );
    return response.data || [];
  },

  async listAllDeposits(filters?: { location_id?: string; limit?: number }): Promise<Deposit[]> {
    const params = new URLSearchParams();
    if (filters?.location_id) params.append('location_id', filters.location_id);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    const q = params.toString();

    const response = await authService.fetchWithAuth(
      `${API_URL}/api/internal-deposits${q ? `?${q}` : ''}`
    );
    return response.data || [];
  },

  // Cierres de caja
  async getLocationBalances(): Promise<LocationBalance[]> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/internal-deposits/closings/location-balances`
    );
    return response.data || [];
  },

  async listClosings(filters?: { location_id?: string; status?: string; limit?: number }): Promise<CashClosing[]> {
    const params = new URLSearchParams();
    if (filters?.location_id) params.append('location_id', filters.location_id);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    const q = params.toString();

    const response = await authService.fetchWithAuth(
      `${API_URL}/api/internal-deposits/closings${q ? `?${q}` : ''}`
    );
    return response.data || [];
  },

  async getClosing(id: string): Promise<CashClosing> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/internal-deposits/closings/${id}`
    );
    return response.data;
  },

  async createClosing(data: {
    location_id: string;
    currency_type_id: string;
    closing_date: string;
    physical_count: number;
    justification?: string;
  }): Promise<CashClosing> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/internal-deposits/closings`,
      { method: 'POST', body: JSON.stringify(data) }
    );
    return response.data;
  },

  async applyAdjustment(id: string): Promise<CashClosing> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/internal-deposits/closings/${id}/adjust`,
      { method: 'POST' }
    );
    return response.data;
  },

  async approveClosing(id: string): Promise<CashClosing> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/internal-deposits/closings/${id}/approve`,
      { method: 'POST' }
    );
    return response.data;
  },
};